// Alerte commande restaurateur — polling + son en boucle + modal grand format + impression Bluetooth
// + Web Push VAPID (veille profonde) + historique impressions + son personnalisable.
// Conçu pour tablette Android en mode PWA plein écran.
import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Bluetooth, BellRing, Check, X, Printer, Clock, User, MapPin, Bell, Settings, History } from "lucide-react";
import { printOrderTicket, printTestTicket, connectBluetoothPrinter, getPrinterStatus } from "../lib/btPrinter";
import { subscribeWebPush, unsubscribeWebPush, getPushStatus } from "../lib/webpush";
import { makeAlarmBlobUrl, SOUND_LABELS } from "../lib/alarm";

// Annonce vocale via Web Speech API — silencieux si le navigateur ne supporte pas SpeechSynthesis.
// Aucune dépendance externe. N'interfère PAS avec l'audio d'alarme ni l'imprimante Bluetooth.
function speakNewOrder() {
  try {
    if (typeof window === "undefined") return;
    const synth = window.speechSynthesis;
    if (!synth || typeof window.SpeechSynthesisUtterance !== "function") return;
    const u = new window.SpeechSynthesisUtterance("Nouvelle commande Nold Go");
    u.lang = "fr-FR";
    u.rate = 1;
    u.pitch = 1;
    u.volume = 1;
    // On ne coupe pas la file (l'alarme sonore joue en parallèle, ce sont 2 flux audio distincts).
    synth.speak(u);
  } catch { /* Silencieux — jamais bloquer le flux de commandes */ }
}

function isPendingResto(o) {
  if (!o) return false;
  if (o.resto_status === "accepted" || o.resto_status === "refused") return false;
  if (["completed", "canceled", "refused_by_resto", "delivered"].includes(o.status)) return false;
  // "paid" = commande food payée en attente d'acceptation par le resto (Nold Go)
  return ["requested", "pending_resto", "accepted", "assigning", "paid"].includes(o.status) || !o.status;
}


export default function OrderAlert({ restaurantName }) {
  const [current, setCurrent] = useState(null);
  const [prefs, setPrefs] = useState({ sound: "bip", volume: 90, muted: false });
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [pushState, setPushState] = useState({ enabled: false, permission: "default" });
  const [processing, setProcessing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showPrinter, setShowPrinter] = useState(false);
  const [printerState, setPrinterState] = useState(getPrinterStatus());
  const [isAdmin, setIsAdmin] = useState(false); // gate du bouton "Simuler commande" — admin uniquement
  const printMode = "ble"; // Mode Bluetooth direct uniquement (Web Bluetooth), plus de bridges tiers
  const pushDiag = () => {}; // No-op : le panneau diagnostic a été retiré
  const seenIdsRef = useRef(new Set());
  const seededRef = useRef(false);
  const audioRef = useRef(null);
  const alarmUrlRef = useRef(null);
  const wakeLockRef = useRef(null);
  const pollTimerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/restaurateur/alert-prefs");
        setPrefs({ sound: data.sound || "bip", volume: Number(data.volume ?? 90), muted: !!data.muted });
      } catch {
        const local = localStorage.getItem("nold_resto_prefs");
        if (local) try { setPrefs(JSON.parse(local)); } catch {}
      }
    })();
  }, []);

  // useEffect séparé et indépendant : détermine si l'utilisateur est admin (pour afficher
  // le bouton "🧪 Simuler commande"). Isolé du polling pour ne PAS bloquer la sirène
  // en cas d'échec ou de latence de /auth/me.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        const roles = data?.roles || [];
        setIsAdmin(data?.role === "admin" || roles.includes("admin"));
      } catch { setIsAdmin(false); }
    })();
  }, []);

  useEffect(() => {
    if (alarmUrlRef.current) URL.revokeObjectURL(alarmUrlRef.current);
    alarmUrlRef.current = makeAlarmBlobUrl(prefs.sound);
    const a = new Audio(alarmUrlRef.current);
    a.loop = true;
    a.preload = "auto";
    a.volume = Math.max(0, Math.min(1, (prefs.volume || 90) / 100));
    audioRef.current = a;
    localStorage.setItem("nold_resto_prefs", JSON.stringify(prefs));
    return () => { try { a.pause(); } catch {} };
  }, [prefs]);

  useEffect(() => {
    const unlock = () => {
      const a = audioRef.current;
      if (!a) return;
      const v = a.volume;
      a.volume = 0;
      a.play().then(() => { a.pause(); a.currentTime = 0; a.volume = v; setAudioBlocked(false); }).catch(() => setAudioBlocked(true));
    };
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/restaurateur/orders?limit=30");
      const items = data?.items || [];
      // 1er poll (mount) — comportement UberEats/Deliveroo :
      //   • Si une ou plusieurs commandes "paid" sont DÉJÀ en attente (le resto vient
      //     d'ouvrir la page alors que Pascal a commandé pendant qu'elle était fermée),
      //     on déclenche IMMÉDIATEMENT l'alarme + modal sur la plus récente.
      //   • Les commandes "awaiting_payment" (avant Stripe) NE sont PAS seeded : elles
      //     déclencheront l'alarme quand elles passeront à "paid".
      //   • Les commandes déjà "accepted" ou "refused" par le resto sont juste seeded.
      if (!seededRef.current) {
        seededRef.current = true;
        const pendings = items.filter(isPendingResto);
        pendings.forEach((o) => seenIdsRef.current.add(o.id));
        // items déjà triés DESC par created_at côté backend → pendings[0] = la plus récente
        if (pendings.length > 0) openAlert(pendings[0]);
        return;
      }
      const nouvelle = items.find((o) => isPendingResto(o) && !seenIdsRef.current.has(o.id));
      if (nouvelle) {
        seenIdsRef.current.add(nouvelle.id);
        speakNewOrder();  // TTS "Nouvelle commande Nold Go" — après seeding uniquement
        openAlert(nouvelle);
      }
      // On ne track que les pending — les non-pending peuvent encore transitionner (paiement).
      items.forEach((o) => { if (isPendingResto(o)) seenIdsRef.current.add(o.id); });
    } catch {}
    // eslint-disable-next-line
  }, []);

  // Tentative de reconnexion silencieuse au montage : retrouve l'imprimante déjà autorisée
  // dans cette app (via navigator.bluetooth.getDevices, Chrome 89+) sans popup.
  useEffect(() => {
    (async () => {
      if (getPrinterStatus().connected) return;
      try {
        if ("bluetooth" in navigator && typeof navigator.bluetooth.getDevices === "function") {
          const known = await navigator.bluetooth.getDevices();
          if (known.length > 0) {
            await connectBluetoothPrinter();
            setPrinterState(getPrinterStatus());
          }
        }
      } catch { /* silencieux — l'utilisateur peut appuyer sur « Appairer » manuellement */ }
    })();
  }, []);

  useEffect(() => {
    getPushStatus().then(setPushState);
    const onMsg = (e) => {
      if (e.data?.type === "nold-push" && e.data?.payload?.sound) poll();
    };
    navigator.serviceWorker?.addEventListener("message", onMsg);
    return () => navigator.serviceWorker?.removeEventListener("message", onMsg);
  }, [poll]);

  useEffect(() => {
    poll();
    pollTimerRef.current = setInterval(poll, 6000);
    return () => clearInterval(pollTimerRef.current);
  }, [poll]);

  const openAlert = (order) => {
    setCurrent(order);
    if ("Notification" in window && Notification.permission === "default") Notification.requestPermission();
    if ("wakeLock" in navigator) {
      navigator.wakeLock.request("screen").then((wl) => { wakeLockRef.current = wl; }).catch(() => {});
    }
    if ("vibrate" in navigator) navigator.vibrate([400, 200, 400, 200, 400]);
    if (!prefs.muted && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => setAudioBlocked(true));
    }
  };

  const stopAlarm = () => {
    try { audioRef.current?.pause(); if (audioRef.current) audioRef.current.currentTime = 0; } catch {}
    if (wakeLockRef.current) { try { wakeLockRef.current.release(); } catch {} wakeLockRef.current = null; }
  };

  const savePrefs = async (updates) => {
    const merged = { ...prefs, ...updates };
    setPrefs(merged);
    if (merged.muted) stopAlarm();
    try { await api.post("/restaurateur/alert-prefs", updates); } catch {}
  };

  const logPrint = async (payload) => {
    try { await api.post("/restaurateur/print-logs", payload); } catch {}
  };

  const accept = async () => {
    if (!current || processing) return;
    setProcessing(true);
    stopAlarm();
    try {
      await api.post(`/restaurateur/orders/${current.id}/accept`);
      toast.success("✅ Commande acceptée");
      const t0 = Date.now();
      try {
        await printOrderTicket({ ...current, restaurant_name: restaurantName || current.restaurant_name });
        const dur = Date.now() - t0;
        toast.success("🖨️ Ticket imprimé");
        logPrint({ order_id: current.id, status: "success", printer_name: getPrinterStatus().name || "", duration_ms: dur });
      } catch (e) {
        const dur = Date.now() - t0;
        toast.error("Impression échouée — " + (e?.message || ""));
        logPrint({ order_id: current.id, status: "failed", printer_name: getPrinterStatus().name || "", error: String(e?.message || e), duration_ms: dur });
      }
      setCurrent(null);
    } catch (e) { toast.error(apiError(e)); }
    finally { setProcessing(false); }
  };

  const refuse = async () => {
    if (!current || processing) return;
    const reason = window.prompt("Motif du refus (facultatif) :", "");
    if (reason === null) return;
    setProcessing(true);
    stopAlarm();
    try {
      await api.post(`/restaurateur/orders/${current.id}/refuse`, { reason });
      toast.info("Commande refusée");
      setCurrent(null);
    } catch (e) { toast.error(apiError(e)); }
    finally { setProcessing(false); }
  };

  const pairPrinter = async () => {
    try {
      if (!("bluetooth" in navigator)) {
        toast.error("Web Bluetooth non supporté");
        return;
      }
      await connectBluetoothPrinter();
      const st = getPrinterStatus();
      setPrinterState(st);
      toast.success("🖨️ Imprimante Bluetooth appairée");
    } catch (e) {
      const msg = e?.message || String(e);
      toast.error(msg.slice(0, 120));
    }
  };

  const testPrint = async () => {
    if (printMode === "ble" && !printerState.connected) {
      // Tentative de reconnexion automatique via getDevices() (Chrome 89+) —
      // permet de retrouver une imprimante déjà autorisée sans popup.
      try {
        toast.info("Reconnexion imprimante…");
        await connectBluetoothPrinter();
        setPrinterState(getPrinterStatus());
      } catch (e) {
        toast.error(e?.message || "Appairez d'abord l'imprimante via le bouton « Appairer imprimante ».");
        return;
      }
    }
    const t0 = Date.now();
    try {
      await printTestTicket(restaurantName || "Nold Go");
      const dur = Date.now() - t0;
      toast.success(`🖨️ Ticket test imprimé (${dur} ms)`);
      logPrint({ order_id: null, status: "success", printer_name: printerState.name || printMode, duration_ms: dur });
    } catch (e) {
      const dur = Date.now() - t0;
      toast.error("Impression test échouée : " + (e?.message || ""));
      logPrint({ order_id: null, status: "failed", printer_name: printerState.name || printMode, error: String(e?.message || e), duration_ms: dur });
    }
  };

  const togglePush = async () => {
    try {
      if (pushState.enabled) { await unsubscribeWebPush(); toast.info("Notifications push désactivées"); }
      else { await subscribeWebPush(); toast.success("🔔 Notifications push activées — même en veille"); }
      setPushState(await getPushStatus());
    } catch (e) { toast.error(e?.message || "Impossible d'activer les notifications"); }
  };

  const testPush = async () => {
    try { const { data } = await api.post("/restaurateur/push/test"); toast.success(`Notif envoyée à ${data.sent || 0} appareil(s)`); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <>
      <div className="fixed bottom-4 right-4 z-40 flex flex-col gap-2 items-end" data-testid="order-alert-toolbar">
        {/* Bouton unique qui regroupe les paramètres imprimante — dashboard restaurateur épuré */}
        <button data-testid="toggle-printer-menu" onClick={() => setShowPrinter((v) => !v)}
          className={`h-10 px-3 rounded-full shadow-lg text-xs font-bold inline-flex items-center gap-1.5 ${printerState.connected ? "bg-[#16a34a] text-white" : "bg-white text-[#1A1A1A] border border-[#E2DFD8]"}`}>
          <Printer className="w-4 h-4" />
          🖨️ Paramètres imprimante
          <span className="ml-1 text-[10px] opacity-75">{showPrinter ? "▲" : "▼"}</span>
        </button>

        {showPrinter && (
          <div data-testid="printer-settings-panel" className="w-[260px] rounded-xl border border-[#E2DFD8] bg-white shadow-2xl p-3 flex flex-col gap-2">
            <div className="text-[11px] font-bold text-[#1A1A1A] pb-1 border-b border-[#E2DFD8]">🖨️ Paramètres imprimante</div>
            <button data-testid="pair-printer" onClick={pairPrinter}
              className={`h-9 px-3 rounded-full text-[11px] font-bold inline-flex items-center gap-1.5 justify-center ${printerState.connected ? "bg-[#16a34a] text-white" : "bg-white text-[#1A1A1A] border border-[#E2DFD8]"}`}>
              <Printer className="w-3.5 h-3.5" />
              {printerState.connected ? "Imprimante prête" : "Appairer imprimante"}
            </button>
            {printerState.connected && (
              <button data-testid="test-print" onClick={testPrint}
                className="h-9 px-3 rounded-full text-[11px] font-bold inline-flex items-center gap-1.5 justify-center bg-[#F26419] hover:bg-[#D85312] text-white">
                <Printer className="w-3.5 h-3.5" /> Ticket test complet
              </button>
            )}
          </div>
        )}
        {isAdmin && (
          <button data-testid="simulate-order" onClick={async () => {
            try {
              await api.post("/restaurateur/orders/test-simulate");
              toast.success("🧪 Commande test créée — la sirène va sonner dans quelques secondes");
            } catch (e) { toast.error(apiError(e)); }
          }}
            className="h-10 px-3 rounded-full shadow-lg text-xs font-bold inline-flex items-center gap-1.5 bg-[#7C3AED] hover:bg-[#6D28D9] text-white">
            🧪 Simuler commande (admin)
          </button>
        )}
        <button data-testid="toggle-push" onClick={togglePush}
          className={`h-10 px-3 rounded-full shadow-lg text-xs font-bold inline-flex items-center gap-1.5 ${pushState.enabled ? "bg-[#F26419] text-white" : "bg-white text-[#1A1A1A] border border-[#E2DFD8]"}`}>
          <Bell className="w-4 h-4" />
          {pushState.enabled ? "Push actif (veille)" : "Activer alertes push"}
        </button>
        <button data-testid="open-settings" onClick={() => setShowSettings(true)}
          className="h-10 px-3 rounded-full shadow-lg text-xs font-bold inline-flex items-center gap-1.5 bg-white text-[#1A1A1A] border border-[#E2DFD8]">
          <Settings className="w-4 h-4" /> Alerte : {SOUND_LABELS[prefs.sound] || "Bip"} · {prefs.volume}%
        </button>
        <button data-testid="open-history" onClick={() => setShowHistory(true)}
          className="h-10 px-3 rounded-full shadow-lg text-xs font-bold inline-flex items-center gap-1.5 bg-white text-[#1A1A1A] border border-[#E2DFD8]">
          <History className="w-4 h-4" /> Historique tickets
        </button>
        {audioBlocked && !prefs.muted && (
          <button
            onClick={() => {
              const a = audioRef.current;
              if (!a) return;
              const v = a.volume; a.volume = 0;
              a.play().then(() => { a.pause(); a.currentTime = 0; a.volume = v; setAudioBlocked(false); }).catch(() => {});
            }}
            data-testid="resto-unlock-sound"
            className="inline-flex items-center gap-2 h-11 px-4 rounded-full shadow-lg text-sm font-bold bg-[#F26419] hover:bg-[#D94F2B] text-white animate-pulse"
          >
            <BellRing className="w-4 h-4" /> Appuyer pour activer le son
          </button>
        )}
      </div>

      {current && (
        <OrderAlertModal order={current} processing={processing}
          onAccept={accept} onRefuse={refuse}
          printerConnected={printerState.connected} onPair={pairPrinter} />
      )}

      {showSettings && (
        <AlertSettingsModal prefs={prefs} onClose={() => setShowSettings(false)}
          onSave={savePrefs}
          onTestSound={() => {
            const a = audioRef.current;
            if (!a) return;
            a.currentTime = 0;
            a.play().then(() => setTimeout(() => { a.pause(); a.currentTime = 0; }, 1800));
          }}
          onTestPush={testPush} pushEnabled={pushState.enabled} />
      )}

      {showHistory && <PrintHistoryModal onClose={() => setShowHistory(false)} />}
    </>
  );
}


function OrderAlertModal({ order, processing, onAccept, onRefuse, printerConnected, onPair }) {
  const items = order.items || order.cart || [];
  const total = order.total_amount ?? order.total_eur ?? order.fare ?? order.amount_eur ?? 0;
  const requested = order.delivery_at || order.requested_at || order.created_at || null;
  const displayTime = requested ? new Date(requested).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "Dès que possible";
  const clientName = order.passenger_name || order.customer_name || order.client_name || "Client";
  const clientPhone = order.passenger_phone || order.customer_phone || "";
  const itemQty = (it) => it.qty ?? it.quantity ?? 1;
  const itemPrice = (it) => Number((it.price_eur ?? it.price ?? 0)) * itemQty(it);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 md:p-8" data-testid="order-alert-modal">
      <div className="bg-white rounded-3xl w-full max-w-3xl shadow-2xl overflow-hidden">
        <div className="bg-[#F26419] text-white p-5 md:p-6 flex items-center gap-3 animate-pulse">
          <BellRing className="w-8 h-8 md:w-10 md:h-10" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wider opacity-90">Nouvelle commande</div>
            <div className="font-black text-2xl md:text-3xl leading-tight">Commande #{(order.id || "").slice(0, 8).toUpperCase()}</div>
          </div>
          <div className="text-right">
            <div className="text-4xl md:text-5xl font-black">{Number(total).toFixed(2)} €</div>
          </div>
        </div>

        <div className="p-5 md:p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <InfoTile icon={User} label="Client" value={clientName + (clientPhone ? ` · ${clientPhone}` : "")} />
            <InfoTile icon={Clock} label="Livraison souhaitée" value={displayTime} />
            <InfoTile icon={MapPin} label="Adresse" value={order.dropoff_address || order.delivery_address || "—"} />
          </div>

          <div className="rounded-2xl bg-[#FDF8F0] border border-[#E2DFD8] p-4">
            <div className="text-xs font-bold uppercase tracking-wider text-[#75807A] mb-2">Plats commandés ({items.length})</div>
            {items.length === 0 ? (
              <div className="text-sm text-[#75807A] italic">Aucun détail des plats — commande simple ({Number(total).toFixed(2)} €).</div>
            ) : (
              <div className="divide-y divide-[#E2DFD8]">
                {items.map((it, i) => (
                  <div key={i} className="flex items-center justify-between py-2">
                    <div className="min-w-0">
                      <div className="font-bold text-lg md:text-xl text-[#1A1A1A]">{it.name || it.title || "Plat"}</div>
                      {it.note && <div className="text-xs text-[#75807A]">{it.note}</div>}
                    </div>
                    <div className="flex items-center gap-3 shrink-0 ml-3">
                      <div className="w-12 h-12 rounded-full bg-[#F26419] text-white font-black text-xl flex items-center justify-center">×{itemQty(it)}</div>
                      <div className="font-bold text-lg text-[#F26419] min-w-[80px] text-right">{itemPrice(it).toFixed(2)} €</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {!printerConnected && (
            <button onClick={onPair} className="w-full h-11 rounded-xl bg-white border-2 border-dashed border-[#F26419] text-[#F26419] font-bold text-sm inline-flex items-center justify-center gap-2">
              <Bluetooth className="w-4 h-4" /> Appairer l'imprimante Bluetooth avant d'accepter (facultatif)
            </button>
          )}

          <div className="grid grid-cols-2 gap-3 pt-2">
            <button data-testid="order-refuse" onClick={onRefuse} disabled={processing}
              className="h-16 md:h-20 rounded-2xl bg-white border-4 border-[#B91C1C] text-[#B91C1C] font-black text-xl md:text-2xl inline-flex items-center justify-center gap-3 hover:bg-[#B91C1C]/5 disabled:opacity-60">
              <X className="w-7 h-7" /> Refuser
            </button>
            <button data-testid="order-accept" onClick={onAccept} disabled={processing}
              className="h-16 md:h-20 rounded-2xl bg-[#16a34a] hover:bg-[#127a37] text-white font-black text-xl md:text-2xl inline-flex items-center justify-center gap-3 shadow-lg disabled:opacity-60">
              <Check className="w-7 h-7" /> {processing ? "Envoi…" : "Accepter"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function AlertSettingsModal({ prefs, onClose, onSave, onTestSound, onTestPush, pushEnabled }) {
  const [local, setLocal] = useState(prefs);
  const commit = () => { onSave(local); onClose(); };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="alert-settings-modal">
      <div className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-xs text-[#75807A] font-semibold uppercase tracking-wider">Alerte commande</div>
            <h3 className="font-head font-black text-xl text-[#1A1A1A]">Personnaliser la sonnerie</h3>
          </div>
          <button onClick={onClose} className="text-[#75807A] hover:text-[#1A1A1A] p-2 rounded-lg hover:bg-[#F0EBE1]"><X className="w-4 h-4" /></button>
        </div>

        <div className="space-y-3">
          <div>
            <div className="text-xs font-bold text-[#75807A] uppercase tracking-wider mb-2">Sonnerie</div>
            <div className="grid grid-cols-3 gap-2">
              {["bip", "grelot", "melodie"].map((k) => (
                <button key={k} data-testid={`sound-${k}`} onClick={() => setLocal({ ...local, sound: k })}
                  className={`h-14 rounded-xl border-2 font-bold text-sm ${local.sound === k ? "border-[#F26419] bg-[#F26419]/10 text-[#B85014]" : "border-[#E2DFD8] bg-white text-[#75807A]"}`}>
                  {SOUND_LABELS[k]}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-bold text-[#75807A] uppercase tracking-wider mb-1 flex items-center justify-between">
              <span>Volume maximum</span>
              <span className="text-[#F26419]">{local.volume}%</span>
            </div>
            <input data-testid="volume-slider" type="range" min="0" max="100" step="5" value={local.volume}
              onChange={(e) => setLocal({ ...local, volume: Number(e.target.value) })}
              className="w-full accent-[#F26419]" />
          </div>
          <label className="flex items-center gap-2 text-sm text-[#1A1A1A]">
            <input type="checkbox" checked={!!local.muted}
              onChange={(e) => setLocal({ ...local, muted: e.target.checked })} className="w-4 h-4 accent-[#F26419]" />
            Couper le son (garder uniquement l'affichage + vibration)
          </label>
          <div className="flex flex-wrap gap-2 pt-1">
            <button data-testid="test-sound" onClick={() => { onSave(local); setTimeout(onTestSound, 150); }}
              className="h-10 px-4 rounded-xl bg-[#F26419] text-white font-bold text-sm">🔊 Tester le son</button>
            <button data-testid="test-push" onClick={onTestPush} disabled={!pushEnabled}
              className="h-10 px-4 rounded-xl bg-white border-2 border-[#F26419] text-[#F26419] font-bold text-sm disabled:opacity-50">🔔 Tester push</button>
          </div>
        </div>

        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="h-10 px-4 rounded-xl bg-white border border-[#E2DFD8] text-[#75807A] font-bold text-sm">Annuler</button>
          <button data-testid="save-alert-settings" onClick={commit} className="h-10 px-4 rounded-xl bg-[#F26419] text-white font-bold text-sm">Enregistrer</button>
        </div>
      </div>
    </div>
  );
}


function PrintHistoryModal({ onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/restaurateur/print-logs?limit=30");
        setItems(data.items || []);
      } catch {}
      setLoading(false);
    })();
  }, []);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="print-history-modal">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-xs text-[#75807A] font-semibold uppercase tracking-wider">Historique</div>
            <h3 className="font-head font-black text-xl text-[#1A1A1A]">Impressions récentes ({items.length})</h3>
          </div>
          <button onClick={onClose} className="text-[#75807A] hover:text-[#1A1A1A] p-2 rounded-lg hover:bg-[#F0EBE1]"><X className="w-4 h-4" /></button>
        </div>
        {loading ? <div className="text-center py-6 text-sm text-[#75807A]">Chargement…</div> :
          items.length === 0 ? <div className="rounded-lg bg-[#FDF8F0] p-6 text-center text-sm text-[#75807A]">Aucune impression enregistrée pour le moment.</div> :
          <div className="space-y-2">
            {items.map((it) => (
              <div key={it.id} className="rounded-xl border border-[#E2DFD8] p-3 flex items-start justify-between gap-2" data-testid={`print-log-${it.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-bold text-[#1A1A1A]">
                    {it.status === "success" ? "✅ Succès" : "❌ Échec"} — Commande #{(it.order_id || "").slice(0, 8).toUpperCase() || "—"}
                  </div>
                  <div className="text-xs text-[#75807A] mt-0.5">
                    {new Date(it.created_at).toLocaleString("fr-FR")} · {it.printer_name || "—"}{it.duration_ms ? ` · ${it.duration_ms} ms` : ""}
                  </div>
                  {it.error && <div className="text-xs text-[#B91C1C] mt-1 break-all">{it.error}</div>}
                </div>
                <div className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full shrink-0 ${it.status === "success" ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#FEE2E2] text-[#B91C1C]"}`}>{it.status}</div>
              </div>
            ))}
          </div>
        }
      </div>
    </div>
  );
}


function InfoTile({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-[#FDF8F0] border border-[#E2DFD8] p-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#75807A] flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</div>
      <div className="text-sm md:text-base font-bold text-[#1A1A1A] mt-0.5 truncate">{value}</div>
    </div>
  );
}
